<template>
  <div class="web-media-preview">
    <div class="preview-container" ref="previewContainer">
      <div class="swiper" :class="{ 'swiper-active': isPlaying }" @click="handleContainerClick">
        <div class="swiper-wrapper" :style="{ transform: `translateX(-${currentIndex * 100}%)` }">
          <div
            v-for="(item, index) in list"
            :key="index"
            class="swiper-item"
            :class="{ active: index === currentIndex }"
          >
            <!-- 图片展示 -->
            <img
              v-if="isImageUrl(item.url)"
              class="media-item image-item"
              :src="item.url"
              alt="Preview image"
              @click="previewImage(index)"
            />
            <!-- 视频展示 -->
            <div v-else class="media-item video-container">
              <video
                :id="`video-${item.id || index}`"
                class="video-item"
                :src="item.url"
                :controls="true"
                :autoplay="index === currentIndex && isPlaying"
                :loop="true"
                @play="handleVideoPlay(item.id || index)"
                @pause="handleVideoPause"
                @ended="handleVideoEnded"
              ></video>
              <div
                v-if="!isPlaying && index === currentIndex"
                class="play-btn"
                @click.stop="playVideo(item.id || index, index)"
              >
                <div class="play-icon"></div>
              </div>
            </div>
          </div>
        </div>
        <!-- 分页指示器 -->
        <!-- <div v-if="list.length > 1" class="pagination">
          <span
            v-for="(_, index) in list"
            :key="index"
            class="pagination-dot"
            :class="{ active: index === currentIndex }"
            @click="setCurrentIndex(index)"
          ></span>
        </div> -->
        <!-- 导航按钮 -->
        <button
          v-if="list.length > 1"
          class="nav-btn prev-btn"
          :class="{ disabled: currentIndex === 0 }"
          :disabled="currentIndex === 0"
          @click.stop="prev"
        >
          &lt;
        </button>
        <button
          v-if="list.length > 1"
          class="nav-btn next-btn"
          :class="{ disabled: currentIndex === list.length - 1 }"
          :disabled="currentIndex === list.length - 1"
          @click.stop="next"
        >
          &gt;
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'

interface MediaItem {
  id?: string | number
  url: string
}

const props = defineProps({
  list: {
    type: Array as () => MediaItem[],
    default: () => []
  },
  currentIndex: {
    type: Number,
    default: 0
  },
  autoplay: {
    type: Boolean,
    default: false
  },
  interval: {
    type: Number,
    default: 3000
  }
})

const emit = defineEmits(['change', 'update:currentIndex'])

const previewContainer = ref<HTMLElement>()
const currentIndex = ref(props.currentIndex)
const isPlaying = ref(false)
const videoId = ref<string | number | null>(null)
const autoplayTimer = ref<number | null>(null)

// 计算属性，用于处理双向绑定
const swiperCurrent = computed({
  get() {
    return currentIndex.value
  },
  set(val) {
    currentIndex.value = val
    emit('update:currentIndex', val)
    emit('change', val)
  }
})

// 监听currentIndex变化
watch(
  () => props.currentIndex,
  (newVal) => {
    currentIndex.value = newVal
  }
)

// 监听autoplay变化
watch(
  () => props.autoplay,
  (newVal) => {
    if (newVal) {
      startAutoplay()
    } else {
      stopAutoplay()
    }
  }
)

// 判断是否为图片URL
function isImageUrl(url: string): boolean {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'svg', 'webp']
  const ext = url.substring(url.lastIndexOf('.') + 1).toLowerCase()
  return imageExtensions.includes(ext)
}

// 预览图片
function previewImage(index: number) {
  const imageUrls = props.list.filter((item) => isImageUrl(item.url)).map((item) => item.url)
  if (imageUrls.length > 0) {
    // 这里可以集成第三方图片预览库，如viewer.js或自定义预览
    console.log('Preview image:', props.list[index].url)
    // 示例：如果使用element-plus的图片预览
    // ElImageViewer.show({
    //   urlList: imageUrls,
    //   initialIndex: index
    // })
  }
}

// 播放视频
function playVideo(id: string | number, index: number) {
  currentIndex.value = index
  isPlaying.value = true
  videoId.value = id

  const videoElement = document.getElementById(`video-${id}`) as HTMLVideoElement
  if (videoElement) {
    videoElement.play()
  }

  stopAutoplay()
}

// 处理视频播放
function handleVideoPlay(id: string | number) {
  videoId.value = id
  isPlaying.value = true
  stopAutoplay()
}

// 处理视频暂停
function handleVideoPause() {
  isPlaying.value = false
  startAutoplay()
}

// 处理视频结束
function handleVideoEnded() {
  isPlaying.value = false
  startAutoplay()
}

// 上一张
function prev() {
  if (currentIndex.value > 0) {
    currentIndex.value--
    swiperCurrent.value = currentIndex.value
    resetVideoState()
  }
}

// 下一张
function next() {
  if (currentIndex.value < props.list.length - 1) {
    currentIndex.value++
    swiperCurrent.value = currentIndex.value
    resetVideoState()
  }
}

// 重置视频状态
function resetVideoState() {
  // 停止当前播放的视频
  if (videoId.value) {
    const videoElement = document.getElementById(`video-${videoId.value}`) as HTMLVideoElement
    if (videoElement) {
      videoElement.pause()
    }
  }

  videoId.value = null
  isPlaying.value = false

  // 检查当前项是否为视频
  if (currentIndex.value >= 0 && currentIndex.value < props.list.length) {
    const currentItem = props.list[currentIndex.value]
    if (currentItem && !isImageUrl(currentItem.url)) {
      // 自动播放视频
      setTimeout(() => {
        playVideo(currentItem.id || currentIndex.value, currentIndex.value)
      }, 100)
    }
  }
}

// 处理容器点击
function handleContainerClick() {
  // 可以添加点击切换播放/暂停的逻辑
}

// 开始自动播放
function startAutoplay() {
  if (props.autoplay && props.list.length > 1 && !isPlaying.value) {
    stopAutoplay()
    autoplayTimer.value = window.setInterval(() => {
      next()
    }, props.interval)
  }
}

// 停止自动播放
function stopAutoplay() {
  if (autoplayTimer.value) {
    clearInterval(autoplayTimer.value)
    autoplayTimer.value = null
  }
}

onMounted(() => {
  // 初始化时检查当前索引是否为视频
  setTimeout(() => {
    const current = props.currentIndex
    if (current >= 0 && current < props.list.length) {
      const currentItem = props.list[current]
      if (currentItem && !isImageUrl(currentItem.url)) {
        playVideo(currentItem.id || current, current)
      }
    }
  }, 300)

  // 启动自动播放
  if (props.autoplay) {
    startAutoplay()
  }
})
</script>

<style lang="less" scoped>
/* 响应式设计 */
@media (width <= 768px) {
  .play-btn {
    width: 60px;
    height: 60px;
  }

  .play-icon {
    border-top: 15px solid transparent;
    border-bottom: 15px solid transparent;
    border-left: 22px solid #fff;
  }

  .nav-btn {
    width: 30px;
    height: 30px;
    font-size: 16px;
  }

  .prev-btn {
    left: 10px;
  }

  .next-btn {
    right: 10px;
  }
}

.web-media-preview {
  position: relative;
  width: 100%;
  height: 100%;
}

.preview-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.swiper {
  position: relative;
  width: 100%;
  height: 100%;
  transition: all 0.3s ease;
}

.swiper-wrapper {
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.5s ease;
}

.swiper-item {
  position: relative;
  height: 100%;
  overflow: hidden;
  flex: 0 0 100%;
}

.media-item {
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
}

.image-item {
  background-color: #fff;
}

.video-container {
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #000;
}

.video-item {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* 播放按钮样式 */
.play-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 10;
  display: flex;
  width: 80px;
  height: 80px;
  cursor: pointer;
  background-color: rgb(0 0 0 / 60%);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: all 0.3s ease;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: rgb(0 0 0 / 80%);
    transform: translate(-50%, -50%) scale(1.1);
  }
}

.play-icon {
  width: 0;
  height: 0;
  margin-left: 5px;
  border-top: 20px solid transparent;
  border-bottom: 20px solid transparent;
  border-left: 30px solid #fff;
}

/* 分页指示器 */
.pagination {
  position: absolute;
  right: 0;
  bottom: 20px;
  left: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  gap: 8px;
}

.pagination-dot {
  width: 8px;
  height: 8px;
  cursor: pointer;
  background-color: rgb(255 255 255 / 50%);
  border-radius: 50%;
  transition: all 0.3s ease;

  &.active {
    width: 20px;
    background-color: #fff;
    border-radius: 4px;
  }
}

/* 导航按钮 */
.nav-btn {
  position: absolute;
  top: 50%;
  z-index: 10;
  display: flex;
  width: 40px;
  height: 40px;
  font-size: 20px;
  color: #fff;
  cursor: pointer;
  background-color: rgb(0 0 0 / 50%);
  border: none;
  border-radius: 50%;
  transform: translateY(-50%);
  transition: all 0.3s ease;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: rgb(0 0 0 / 80%);
  }

  &.disabled {
    cursor: not-allowed;
    background-color: rgb(0 0 0 / 20%);

    &:hover {
      background-color: rgb(0 0 0 / 20%);
    }
  }
}

.prev-btn {
  left: 20px;
}

.next-btn {
  right: 20px;
}
</style>
